import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { FlexGrid, FlexGridColumn, FlexGridCellTemplate } from '@grapecity/wijmo.react.grid';
import TextComponent from 'components/Input/TextComponent';
import SelectComponent from 'components/Input/SelectComponent';
import QuoteDetailModal from './QuoteDetailModal';
import './QuoteCopyModal.css';

// 担当者モックデータ
const mockPlaners = [
  { id: '001', name: '山田太郎' },
  { id: '002', name: '佐藤花子' },
  { id: '003', name: '鈴木一郎' },
];

// 工事種別モックデータ
const mockKoujiSyu = [
  { id: '01', name: '仮設工事' },
  { id: '02', name: '土工事' },
  { id: '03', name: '解体撤去工事' },
  { id: '04', name: '門周り工事' },
];

// 見積一覧モックデータ
const mockQuoteList = [
  {
    QUOTE_NO: '2024001',
    KOUJI_MEI: 'A邸仮設工事',
    ADDRESS: '東京都港区○○町1-1',
    CLIENT_NAME: '株式会社サンプル',
    TAN_NAME: '山田太郎',
    MITSU_KIN: 2500000,
    MITSU_DATE: '2024/01/15',
    KOUJI_SYU: '01,02',
  },
  {
    QUOTE_NO: '2024002',
    KOUJI_MEI: 'B邸土工事',
    ADDRESS: '東京都港区△△町2-2',
    CLIENT_NAME: '田中商事株式会社',
    TAN_NAME: '佐藤花子',
    MITSU_KIN: 3800000,
    MITSU_DATE: '2024/01/20',
    KOUJI_SYU: '03,02',
  },
  {
    QUOTE_NO: '2024003',
    KOUJI_MEI: 'Cビル門周り工事',
    ADDRESS: '東京都港区△△町3-3',
    CLIENT_NAME: '有限会社テスト',
    TAN_NAME: '鈴木一郎',
    MITSU_KIN: 1500000,
    MITSU_DATE: '2024/02/01',
    KOUJI_SYU: '04',
  },
  {
    QUOTE_NO: '2024004',
    KOUJI_MEI: 'D邸門周り工事',
    ADDRESS: '東京都港区△△町4-4',
    CLIENT_NAME: '個人 山本様',
    TAN_NAME: '山田太郎',
    MITSU_KIN: 980000,
    MITSU_DATE: '2024/02/10',
    KOUJI_SYU: '01,04',
  },
];

const getValue = (v) => {
  if (v?.target?.value !== undefined) return v.target.value;

  if (v?.value !== undefined) return v.value;

  return v ?? '';
};

function QuoteCopyModal({ show, setShow, onCopy }) {
  const [searchConditions, setSearchConditions] = useState({
    tanCd: '001',
    koujiMei: '',
    mitsuDateFrom: '',
    mitsuDateTo: '',
    koujiSyu: '',
  });

  //検索結果
  const [searchResults, setSearchResults] = useState([]);

  //詳細モーダル
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const selectedTanName = useMemo(() => {
    const tan = mockPlaners.find((p) => p.id === searchConditions.tanCd);
    return tan?.name ?? '';
  }, [searchConditions.tanCd]);

  const handleSearch = () => {
    let filtered = [...mockQuoteList];

    //営業担当
    if (searchConditions.tanCd && selectedTanName) {
      filtered = filtered.filter((q) => q.TAN_NAME === selectedTanName);
    }

    // 工事名（部分一致）
    if (searchConditions.koujiMei) {
      filtered = filtered.filter((q) =>
        q.KOUJI_MEI.includes(searchConditions.koujiMei)
      );
    }

    //工事種別（含む）
    if (searchConditions.koujiSyu) {
      filtered = filtered.filter((q) =>
        q.KOUJI_SYU.includes(searchConditions.koujiSyu)
      );
    }

    //見積日（開始）
    if (searchConditions.mitsuDateFrom) {
      const from = searchConditions.mitsuDateFrom.replace(/-/g, '/');
      filtered = filtered.filter((q) => q.MITSU_DATE >= from);
    }

    //見積日（終了）
    if (searchConditions.mitsuDateTo) {
      const to = searchConditions.mitsuDateTo.replace(/-/g, '/');
      filtered = filtered.filter((q) => q.MITSU_DATE <= to);
    }

    setSearchResults(filtered);
  };

  useEffect(() => {
    if (show) {
      handleSearch();
    }
  }, [show]);

  const handleShowDetail = (quote) => {
    setSelectedQuote(quote);
    setShowDetailModal(true);
  };

  const handleClose = () => {
    setShow(false);
    setSearchConditions({
      tanCd: '001',
      koujiMei: '',
      mitsuDateFrom: '',
      mitsuDateTo: '',
      koujiSyu: '',
    });
    setSearchResults([]);
    setSelectedQuote(null);
    setShowDetailModal(false);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        animation={false}
        backdrop="static"
        enforceFocus={false}
        restoreFocus={false}
      >
        <Modal.Header>
          <Modal.Title>見積書コピー</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* 検索条件 */}
          <Row>
            <Col>
              <Row>
                <Col className="pl-3" md="2">
                  <Form.Group>
                    <label>営業担当</label>
                    <SelectComponent
                      value={searchConditions.tanCd}
                      onChange={(v) =>
                        setSearchConditions({
                          ...searchConditions,
                          tanCd: getValue(v),
                        })
                      }
                    >
                      {mockPlaners.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </SelectComponent>
                  </Form.Group>
                </Col>

                <Col className="pr-1" md="3">
                  <Form.Group>
                    <label>工事名</label>
                    <TextComponent
                      placeholder="部分一致で検索します"
                      type="text"
                      value={searchConditions.koujiMei}
                      onChange={(v) =>
                        setSearchConditions({
                          ...searchConditions,
                          koujiMei: getValue(v),
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col className="pr-1" md="2">
                  <Form.Group>
                    <label>見積日（開始）</label>
                    <TextComponent
                      type="date"
                      value={searchConditions.mitsuDateFrom}
                      onChange={(v) =>
                        setSearchConditions({
                          ...searchConditions,
                          mitsuDateFrom: getValue(v),
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col className="pr-1" md="2">
                  <Form.Group>
                    <label>見積日（終了）</label>
                    <TextComponent
                      type="date"
                      value={searchConditions.mitsuDateTo}
                      onChange={(v) =>
                        setSearchConditions({
                          ...searchConditions,
                          mitsuDateTo: getValue(v),
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col className="pr-1" md="2">
                  <Form.Group>
                    <label>工事種別</label>
                    <SelectComponent
                      value={searchConditions.koujiSyu}
                      onChange={(v) =>
                        setSearchConditions({
                          ...searchConditions,
                          koujiSyu: getValue(v),
                        })
                      }
                    >
                      <option value="">すべて</option>
                      {mockKoujiSyu.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.name}
                        </option>
                      ))}
                    </SelectComponent>
                  </Form.Group>
                </Col>

                <Col style={{ paddingTop: '30px' }}>
                  <Button
                    variant="success"
                    className="btn-fill"
                    size="sm"
                    onClick={handleSearch}
                  >
                    検索
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* 見積一覧 */}
          <Row className="mt-3">
            <Col>件数：{searchResults.length}件</Col>
          </Row>

          <Row>
            <Col>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <FlexGrid
                  itemsSource={searchResults}
                  selectionMode="Row"
                  headersVisibility="Column"
                  allowSorting={false}
                  loadedRows={(grid) => {
                    grid.rows.forEach((ele) => {
                      ele.height = 38;
                    });
                  }}
                >
                  <FlexGridColumn
                    binding="KOUJI_MEI"
                    header="工事名（社内呼称）"
                    width="*"
                    isReadOnly={true}
                  />
                  <FlexGridColumn
                    binding="ADDRESS"
                    header="住所"
                    width={300}
                    isReadOnly={true}
                  />
                  <FlexGridColumn
                    binding="CLIENT_NAME"
                    header="顧客名"
                    width={250}
                    isReadOnly={true}
                  />
                  <FlexGridColumn
                    binding="TAN_NAME"
                    header="営業担当"
                    width={100}
                    isReadOnly={true}
                  />
                  <FlexGridColumn
                    binding="MITSU_KIN"
                    header="金額"
                    format="c0"
                    width={120}
                    isReadOnly={true}
                  />
                  <FlexGridColumn
                    binding="MITSU_DATE"
                    header="見積日"
                    width={100}
                    isReadOnly={true}
                  />
                  <FlexGridColumn
  header="工事種別"
  width={180}
  isReadOnly={true}
>
  <FlexGridCellTemplate
    cellType="Cell"
    template={(cell) => {
      const ids = String(cell.item?.KOUJI_SYU ?? '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const names = ids
        .map(id => mockKoujiSyu.find(k => k.id === id)?.name ?? id)
        .join('、');

      return <span>{names}</span>;
    }}
  />
</FlexGridColumn>
                  <FlexGridColumn
                    binding=""
                    header=""
                    width={55}
                    isReadOnly={true}
                  >
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <Button
                          variant="primary"
                          size="sm"
                          className="btn-fill"
                          onClick={() => handleShowDetail(cell.item)}
                        >
                          詳細
                        </Button>
                      )}
                    />
                  </FlexGridColumn>
                </FlexGrid>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 見積詳細モーダル */}
      <QuoteDetailModal
        show={showDetailModal}
        setShow={setShowDetailModal}
        quote={selectedQuote}
        onCopy={(quoteNo, selectedData) => {
          onCopy?.(quoteNo, selectedData);
          handleClose();
        }}
      />

    </>
  );
}

export default QuoteCopyModal;